import { CheckOutlined, CloseOutlined, WarningFilled } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
} from "antd";
import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { AutocategorizationRuleFragment } from "../../../api/graphql/fragments/autocategorizationRule.generated";
import {
  CreateAutocategorizationRuleInput,
  PaymentFrequency,
} from "../../../api/graphql/schema.generated";
import { Direction, IMessageType } from "../../../types";
import { EXTERNAL_TRANSACTION_CATEGORIES, showMessage } from "../../../utils";
import {
  isGermanRecipientVatId,
  RECIPIENT_VAT_ID_MAX_LENGTH,
  showsRecipientVatIdField,
} from "../../../utils/recipientVatId";
import { BooleanOptionRadioButton } from "../../common/BooleanOptionRadioButton";
import { ResettableBooleanOptionButton } from "../../common/ResettableBooleanOptionButton";
import { ResettableOptionButton } from "../../common/ResettableOptionButton";
import TransactionSplitButton from "../../common/TransactionSplitButton";
import CategorySelect from "../TransactionView/CategorySelect";
import VatCategorySelect from "../TransactionView/VatCategorySelect";
import { AmountInput } from "./AmountInput";
import EmailSearch from "./EmailSearch";
import { RuleConditionStringInput } from "./RuleConditionStringInput";
import RuleSplitView from "./RuleSplitView";
import { FieldBox, FormHeader, StyleFix } from "./styledComponents";
import {
  ruleFormTargetsSingleRecipient,
  RuleSplitLineForm,
  useRuleForm,
} from "./useRuleForm";

export const getFrequencyOptions = (t: TFunction) =>
  [
    PaymentFrequency.MONTHLY,
    PaymentFrequency.QUARTERLY,
    PaymentFrequency.YEARLY,
    PaymentFrequency.NONE,
    PaymentFrequency.NONE_QUARTERLY,
  ].map((value) => ({
    value,
    label: t(`frequency.${value}`),
  }));

export type RuleFormProps = {
  rule?: AutocategorizationRuleFragment;
  title?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
  onSubmit?: (rule: CreateAutocategorizationRuleInput) => void;
  /**
   * Hide the form's own header actions (Cancel / Save). Used when the form is
   * embedded in a modal that provides its own footer actions. The full-page
   * add/edit views leave this off and keep their header buttons.
   */
  hideDefaultActions?: boolean;
  /**
   * id for the underlying <form>, so an external submit button can drive it via
   * `<Button form={formId} htmlType="submit" />` (see antd form association).
   */
  formId?: string;
  /**
   * Layout (24-col antd grid) for the two form columns. Defaults to the
   * full-page layout; a modal passes wider columns to use the available width.
   */
  leftCol?: { span: number; offset: number };
  rightCol?: { span: number; offset: number };
};

export const getRuleFormErrors = (t: TFunction) => ({
  DESCRIPTION: t("form.description.required"),
  SELECTION: t("form.conditions.required"),
  CATEGORY: t("form.category.required"),
  VAT_CATEGORY: t("form.vatCategory.required"),
  RECIPIENT_VAT_ID: t("form.recipientVatId.required"),
  RECIPIENT_VAT_ID_SCOPE: t("form.recipientVatId.scope"),
  RECIPIENT_VAT_ID_DE: t("form.recipientVatId.invalidDe"),
  RECIPIENT_VAT_ID_TOO_LONG: t("form.recipientVatId.tooLong"),
});

// Naming convention shown under the Rule description field so rules get
// consistent, meaningful names.
const RuleNamingHint = ({ t }: { t: TFunction }) => (
  <div style={{ marginTop: 4, lineHeight: 1.7 }}>
    <div>
      {t("form.naming.label")}: <b>{t("form.naming.pattern")}</b>
    </div>
    <div style={{ fontFamily: "monospace" }}>
      {t("form.naming.exampleCustomer")}
    </div>
    <div style={{ fontFamily: "monospace" }}>
      {t("form.naming.exampleGeneral")}
    </div>
  </div>
);

const RuleForm = (props: RuleFormProps) => {
  const { t } = useTranslation(["Rules", "translation"]);
  const ruleFormErrors = getRuleFormErrors(t);
  const { form, setForm, formControls, getRule } = useRuleForm({
    rule: props.rule,
  });

  const leftCol = props.leftCol ?? { span: 8, offset: 2 };
  const rightCol = props.rightCol ?? { span: 8, offset: 1 };

  const [isFormChanged, setIsFormChanged] = useState(false);
  const [triedToSubmit, setTriedToSubmit] = useState(false);

  const isSelectionFilled =
    form.conditions.name.value ||
    form.conditions.ibans.length ||
    form.conditions.description.value;

  const activateSplit = useCallback(() => {
    const nextForm = {
      ...form,
      isSplit: true,
      splitLines: [
        { categoryCode: "", vatCategoryCode: "", amount: 0 },
        { categoryCode: "", vatCategoryCode: "", amount: 0 },
      ] as RuleSplitLineForm[],
    };
    setForm(nextForm);
    setIsFormChanged(true);
  }, [form, setForm]);

  const discardSplit = useCallback(() => {
    const nextForm = {
      ...form,
      isSplit: false,
      splitLines: [] as RuleSplitLineForm[],
    };
    setForm(nextForm);
    setIsFormChanged(true);
  }, [form, setForm]);

  const handleSplitLineChange = useCallback(
    (line: RuleSplitLineForm, index: number) => {
      const updated = [...form.splitLines];
      updated[index] = line;
      setForm({ ...form, splitLines: updated });
      setIsFormChanged(true);
    },
    [form, setForm]
  );

  const handleSplitLineAdd = useCallback(() => {
    setForm({
      ...form,
      splitLines: [
        ...form.splitLines,
        { categoryCode: "", vatCategoryCode: "", amount: 0 },
      ],
    });
    setIsFormChanged(true);
  }, [form, setForm]);

  const handleSplitLineDelete = useCallback(
    (index: number) => {
      const updated = [...form.splitLines];
      updated.splice(index, 1);
      setForm({ ...form, splitLines: updated });
      setIsFormChanged(true);
    },
    [form, setForm]
  );

  return (
    <StyleFix>
      <Spin spinning={props.loading ?? false}>
        <Form
          layout="vertical"
          id={props.formId}
          form={formControls}
          initialValues={form}
          onFinish={() => {
            const rule = getRule();
            if (typeof rule === "string") {
              showMessage({
                type: IMessageType.ERROR,
                content: rule,
                duration: 3,
              });
            } else {
              props.onSubmit?.(rule);
            }
          }}
          onFinishFailed={() => {
            setTriedToSubmit(true);
          }}
          onValuesChange={(_, formValues) => {
            setForm({
              ...formValues,
              isSplit: form.isSplit,
              splitLines: form.splitLines,
            });
            setIsFormChanged(true);
          }}
          requiredMark="optional"
        >
          {!props.hideDefaultActions && (
            <FormHeader>
              {props.title}
              <Space>
                <Popconfirm
                  title={t("form.discardConfirm.title")}
                  onConfirm={() => window.close()}
                  okText={t("form.discardConfirm.ok")}
                  cancelText={t("translation:no")}
                  icon={<WarningFilled style={{ color: "red" }} />}
                  disabled={!isFormChanged}
                  overlayStyle={{ width: 240 }}
                >
                  <Button
                    onClick={() => {
                      !isFormChanged && window.close();
                    }}
                  >
                    {t("form.cancel")}
                  </Button>
                </Popconfirm>
                <Button
                  onClick={formControls.submit}
                  type="primary"
                  data-test="submitButton"
                  icon={<CheckOutlined />}
                >
                  {t("form.save")}
                </Button>
              </Space>
            </FormHeader>
          )}

          <Form.Item name="enabled" hidden>
            <Input />
          </Form.Item>

          <Row>
            <Col span={leftCol.span} offset={leftCol.offset}>
              <Form.Item
                name="description"
                label={t("form.description.label")}
                extra={<RuleNamingHint t={t} />}
                rules={[
                  { required: true, message: ruleFormErrors.DESCRIPTION },
                ]}
              >
                <Input.TextArea
                  placeholder={String(t("form.description.placeholder"))}
                  autoFocus
                />
              </Form.Item>
              <Form.Item
                name="priority"
                label={t("form.priority.label")}
                extra={t("form.priority.hint")}
                required
              >
                <InputNumber min={-5} max={5} precision={0} />
              </Form.Item>
              <Form.Item
                name={["conditions", "accountIds"]}
                label={t("form.accountIds.label")}
              >
                <EmailSearch />
              </Form.Item>
              {props.children}
            </Col>
            <Col span={rightCol.span} offset={rightCol.offset}>
              <FieldBox hasError={triedToSubmit && !isSelectionFilled}>
                <p
                  className={
                    triedToSubmit && !isSelectionFilled
                      ? "ant-form-item-explain-error"
                      : undefined
                  }
                >
                  {ruleFormErrors.SELECTION}
                </p>
                <RuleConditionStringInput
                  name={["conditions", "name"]}
                  label={String(t("form.name.label"))}
                  placeholder={String(t("form.name.placeholder"))}
                  required={!isSelectionFilled || !!form.conditions.name.value}
                  validateStatus=""
                  help=""
                />
                <Form.Item
                  name={["conditions", "ibans"]}
                  label={t("form.iban.label")}
                  rules={[
                    {
                      required:
                        !isSelectionFilled || !!form.conditions.ibans.length,
                    },
                  ]}
                  validateStatus=""
                  help=""
                >
                  <Select
                    mode="tags"
                    placeholder={t("form.iban.placeholder")}
                  />
                </Form.Item>
                <RuleConditionStringInput
                  name={["conditions", "description"]}
                  label={String(t("form.descriptionCondition.label"))}
                  placeholder={String(
                    t("form.descriptionCondition.placeholder")
                  )}
                  required={
                    !isSelectionFilled || !!form.conditions.description.value
                  }
                  validateStatus=""
                  help=""
                />
              </FieldBox>
              <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
                {form.isSplit ? (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: 8,
                      }}
                    >
                      <Button
                        icon={<CloseOutlined style={{ color: "red" }} />}
                        style={{ display: "flex", alignItems: "center" }}
                        onClick={discardSplit}
                        data-test="discardSplitButton"
                      >
                        {t("form.discardSplit")}
                      </Button>
                    </div>
                    <RuleSplitView
                      splitLines={form.splitLines}
                      triedToSubmit={triedToSubmit}
                      transactionAmount={form.conditions.direction || -1}
                      onChange={handleSplitLineChange}
                      onAdd={handleSplitLineAdd}
                      onDelete={handleSplitLineDelete}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <span className="ant-form-item-label">
                        <label>{t("form.category.label")}</label>
                      </span>
                      <TransactionSplitButton onClick={activateSplit} small />
                    </div>
                    <Form.Item
                      name="categoryCode"
                      trigger="onChangeHandler"
                      rules={[
                        { required: true, message: ruleFormErrors.CATEGORY },
                      ]}
                      style={{ marginTop: 0 }}
                    >
                      <CategorySelect
                        name="categoryCode"
                        transactionAmount={form.conditions.direction || -1}
                        disabledOptions={EXTERNAL_TRANSACTION_CATEGORIES}
                      />
                    </Form.Item>

                    <Form.Item
                      name="vatCategoryCode"
                      label={t("form.vatCategory.label")}
                      trigger="onChangeHandler"
                      rules={[
                        {
                          required: true,
                          message: ruleFormErrors.VAT_CATEGORY,
                        },
                      ]}
                    >
                      <VatCategorySelect
                        name="vatCategoryCode"
                        kontaxCategory={form.categoryCode}
                        updateValueOnCategoryChange
                      />
                    </Form.Item>

                    {showsRecipientVatIdField(form.vatCategoryCode) && (
                      <Form.Item
                        name="recipientVatId"
                        label={t("form.recipientVatId.label")}
                        dependencies={[
                          ["conditions", "name"],
                          ["conditions", "ibans"],
                          "vatCategoryCode",
                        ]}
                        rules={[
                          {
                            required: true,
                            message: ruleFormErrors.RECIPIENT_VAT_ID,
                          },
                          ({ getFieldValue }) => ({
                            validator(_rule, value) {
                              const targetsSingleRecipient =
                                ruleFormTargetsSingleRecipient({
                                  ibans: getFieldValue(["conditions", "ibans"]),
                                  name: getFieldValue(["conditions", "name"]),
                                });
                              if (!targetsSingleRecipient) {
                                return Promise.reject(
                                  new Error(
                                    ruleFormErrors.RECIPIENT_VAT_ID_SCOPE
                                  )
                                );
                              }
                              if (isGermanRecipientVatId(value)) {
                                return Promise.reject(
                                  new Error(ruleFormErrors.RECIPIENT_VAT_ID_DE)
                                );
                              }
                              if (
                                (value?.trim().length ?? 0) >
                                RECIPIENT_VAT_ID_MAX_LENGTH
                              ) {
                                return Promise.reject(
                                  new Error(
                                    ruleFormErrors.RECIPIENT_VAT_ID_TOO_LONG
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input
                          placeholder={String(
                            t("form.recipientVatId.placeholder")
                          )}
                        />
                      </Form.Item>
                    )}
                  </>
                )}

                <Form.Item
                  name={["conditions", "direction"]}
                  label={t("form.direction.label")}
                >
                  <ResettableOptionButton
                    options={[
                      {
                        label: t("form.direction.outgoing"),
                        value: Direction.OUTGOING,
                      },
                      {
                        label: t("form.direction.incoming"),
                        value: Direction.INCOMING,
                      },
                    ]}
                  />
                </Form.Item>

                <AmountInput
                  name={["conditions", "amount"]}
                  direction={form.conditions.direction}
                  amount={form.conditions.amount}
                />

                <Form.Item
                  name={["conditions", "vatPaymentFrequencies"]}
                  label={t("form.vatFrequency.label")}
                >
                  <Checkbox.Group
                    options={getFrequencyOptions(t)}
                    name="vatPaymentFrequencies"
                    style={{ maxWidth: "300px" }}
                  />
                </Form.Item>

                <Form.Item
                  name={["conditions", "hasEmployees"]}
                  label={t("form.hasEmployees")}
                >
                  <ResettableBooleanOptionButton />
                </Form.Item>

                <Form.Item
                  name={["conditions", "hasVatNumber"]}
                  label={t("form.hasVatNumber")}
                >
                  <ResettableBooleanOptionButton />
                </Form.Item>

                <Form.Item
                  name={["conditions", "hasInvoices"]}
                  label={t("form.hasInvoices")}
                >
                  <ResettableBooleanOptionButton />
                </Form.Item>

                <Form.Item name="verify" label={t("form.verify")} required>
                  <BooleanOptionRadioButton />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyleFix>
  );
};

export default RuleForm;
